import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { JsonEditor } from 'json-edit-react';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import * as Yup from 'yup';
import { ChargingConfiguration, useGetChargePoint, useUpdateChargePoint } from '../../../../../app/ApiGen';
import { SwitchInput, SwitchValue } from '../../../../../components/CustomFields/Switch';
import { DataErrorHandler } from '../../../../../components/ErrorHandler';
import { errorSnackbar } from '../../../../../components/ErrorSnackbar';
import { Form } from '../../../../../components/Form';
import {
  Card,
  FormActions,
  List,
  ListItem,
  Loading,
  OnSubmit,
  Section,
  ValueComponentProps,
  Workspace,
} from '../../../../../design-system';
import { createSchema, FormFields } from '../../../../../utils/formUtils';
import { normalise } from '../../../../../utils/request';

type FeatureAvailabilityFields = { solarUnlocked: boolean };

const featureAvailabilityFields: FormFields<FeatureAvailabilityFields> = {
  solarUnlocked: {
    id: 'solarUnlocked',
    inputComponent: SwitchInput,
    label: 'Solar unlocked',
    valueComponent: ({ value }: ValueComponentProps<boolean>) => (
      <SwitchValue value={value} labels={{ true: 'Yes', false: 'No' }} />
    ),
  },
};

const featureAvailabilityValidationSchema = createSchema<FeatureAvailabilityFields>({
  solarUnlocked: Yup.boolean().required('Must specify if solar is unlocked'),
});

const defaultChargingConfiguration: ChargingConfiguration = {
  currency: 'NZD' as const,
  tariffs: {
    import: { Flat: { type: 'TIME_OF_USE' as const, amount: '0.3' } },
    export: {},
  },
  periods: { day: [{ start: 0, behaviour: { type: 'Charge' }, importTariff: 'Flat' }] },
  repeats: 'daily',
};

export const ChargingConfigurationEditor: React.VFC = () => {
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();
  const {
    loading: loadingChargePoint,
    data: chargePointData,
    error: chargePointError,
    refetch,
  } = useGetChargePoint({ id });
  const { error: updateError, mutate: mutateChargePoint } = useUpdateChargePoint({ id });

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [chargingConfiguration, setChargingConfiguration] = React.useState<ChargingConfiguration>();
  const [isEditingChargingConfiguration, setIsEditingChargingConfiguration] = React.useState<boolean>(false);
  const [isEditingFeatureAvailability, setIsEditingFeatureAvailability] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!chargePointData) {
      return;
    }
    setChargingConfiguration(chargePointData.data.attributes.chargingConfiguration ?? defaultChargingConfiguration);
  }, [chargePointData]);

  React.useEffect(() => {
    if (updateError) {
      errorSnackbar('Unable to update charge point', closeSnackbar, enqueueSnackbar, updateError.message);
    }
  }, [updateError, closeSnackbar, enqueueSnackbar]);

  const onSaveFeatureAvailability = React.useCallback<OnSubmit<FeatureAvailabilityFields>>(
    async ({ solarUnlocked }) => {
      if (!chargePointData) {
        return;
      }
      await mutateChargePoint({
        data: {
          attributes: { features: { Solar: { unlocked: solarUnlocked } } },
          id: chargePointData.data.id,
          type: 'chargePoints',
        },
      });
      setIsEditingFeatureAvailability(false);
      await refetch();
    },
    [chargePointData, mutateChargePoint, refetch],
  );

  const onSaveChargingConfiguration = React.useCallback(() => {
    setIsEditingChargingConfiguration(true);
    mutateChargePoint({ data: { type: 'chargePoints', id, attributes: { chargingConfiguration } } })
      .then(() => {
        enqueueSnackbar('Charging configuration updated', { variant: 'success' });
      })
      .catch(() => {}) // Already handled by useUpdateChargePoint.
      .finally(() => {
        setIsEditingChargingConfiguration(false);
      });
  }, [mutateChargePoint, id, chargingConfiguration, enqueueSnackbar]);

  const onSetToDefault = React.useCallback(() => {
    setChargingConfiguration(defaultChargingConfiguration);
  }, []);

  if (loadingChargePoint) {
    return (
      <Workspace>
        <Loading />
      </Workspace>
    );
  }

  if (chargePointError || !chargePointData) {
    return <DataErrorHandler description="Unable to load charge point" error={chargePointError} refetch={refetch} />;
  }

  const chargePoint = normalise(chargePointData);
  const isChargePointFlex = chargePoint?.model?.startsWith('E2F');

  return (
    <Workspace>
      <Section title="Charging configuration">
        <Grid container spacing={2}>
          {isChargePointFlex && (
            <Grid item xs={12}>
              <Card title="Feature settings">
                <Form<FeatureAvailabilityFields>
                  fields={featureAvailabilityFields}
                  initialValues={{ solarUnlocked: chargePoint.features.Solar.unlocked }}
                  isEditing={isEditingFeatureAvailability}
                  onSubmit={onSaveFeatureAvailability}
                  setIsEditing={setIsEditingFeatureAvailability}
                  validationSchema={featureAvailabilityValidationSchema}
                />
              </Card>
            </Grid>
          )}
          {chargingConfiguration && (
            <Grid item xs={12}>
              <Card title="Schedule">
                <List>
                  <ListItem key="charging-configuration">
                    <JsonEditor
                      data={chargingConfiguration}
                      setData={(jsonData) => {
                        setChargingConfiguration(jsonData as ChargingConfiguration);
                      }}
                    />
                    <FormActions>
                      <Button
                        color="inherit"
                        disabled={chargingConfiguration === defaultChargingConfiguration}
                        onClick={onSetToDefault}
                        variant="contained"
                      >
                        Set to all day schedule
                      </Button>
                      <Box ml={2} />
                      <Button color="primary" onClick={onSaveChargingConfiguration} type="submit" variant="contained">
                        {isEditingChargingConfiguration ? (
                          <CircularProgress size={26} color="inherit" />
                        ) : (
                          'Save schedule'
                        )}
                      </Button>
                    </FormActions>
                  </ListItem>
                </List>
              </Card>
            </Grid>
          )}
        </Grid>
      </Section>
    </Workspace>
  );
};

export const ChargingConfigurationReadOnly: React.VFC = () => {
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();
  const {
    loading: loadingChargePoint,
    data: chargePointData,
    error: chargePointError,
    refetch,
  } = useGetChargePoint({ id });

  const [chargingConfiguration, setChargingConfiguration] = React.useState<ChargingConfiguration>();

  React.useEffect(() => {
    if (!chargePointData) {
      return;
    }
    setChargingConfiguration(chargePointData.data.attributes.chargingConfiguration ?? defaultChargingConfiguration);
  }, [chargePointData]);

  if (loadingChargePoint) {
    return (
      <Workspace>
        <Loading />
      </Workspace>
    );
  }

  if (chargePointError || !chargePointData) {
    return <DataErrorHandler description="Unable to load charge point" error={chargePointError} refetch={refetch} />;
  }

  return (
    <Workspace>
      <Section title="Charging configuration">
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <List>
                <ListItem>
                  {chargingConfiguration && (
                    <JsonEditor data={chargingConfiguration} restrictAdd restrictDelete restrictDrag restrictEdit />
                  )}
                </ListItem>
              </List>
            </Card>
          </Grid>
        </Grid>
      </Section>
    </Workspace>
  );
};
